import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pencil, Trash2, Plus, Search, X, ChevronDown } from "lucide-react";
import api from "../../../api/axios";
import { useToast } from "../../../context/ToastContext";
import { ErrorMessage, validate } from "../../../components/validation/Validation";
const rules = {
  plan_key: {
    required: true,
    label: "Plan Type",
  },

  plan_name: {
    required: true,
    minLength: 3,
    maxLength: 100,
    label: "Plan Name",
  },

  plan_price: {
    required: true,
    type: "number",
    label: "Price",
    validator: (value) => {
      if (Number(value) < 0) {
        return "Price cannot be negative.";
      }
    },
  },

  plan_billing_cycle: {
    required: true,
    label: "Billing Cycle",
  },

  plan_description: {
    maxLength: 500,
    label: "Description",
  },

  users: {
    required: true,
    type: "number",
    label: "Users Limit",
    validator: (value) => {
      if (Number(value) <= 0) {
        return "Users must be greater than or equal to 1.";
      }
    },
  },

  creatives: {
    required: true,
    type: "number",
    label: "Creatives Limit",
    validator: (value) => {
      if (Number(value) <= 0) {
        return "Creatives must be greater than or equal to 1.";
      }
    },
  },

  campaigns: {
    required: true,
    type: "number",
    label: "Campaigns Limit",
    validator: (value) => {
      if (Number(value) <= 0) {
        return "Campaigns must be greater than or equal to 1.";
      }
    },
  },

  brands: {
    required: true,
    type: "number",
    label: "Brands Limit",
    validator: (value) => {
      if (Number(value) <= 0) {
        return "Brands must be greater than or equal to 1.";
      }
    },
  },

  plan_status: {
    required: true,
    label: "Status",
  },
};

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletePlan, setDeletePlan] = useState(null);
  const [errors, setErrors] = useState({});

  const { success, error } = useToast();

  const [isStatusOpen, setIsStatusOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".status-dropdown")) {
        setIsStatusOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);

      const response = await api.get("/plan/list");
      if (response?.data?.success) {
        setPlans(response.data.data || []);
      } else {
        setPlans([]);
      }
    } catch (err) {
      console.log(err);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = useMemo(() => {
    return plans.filter((item) => {
      const matchesSearch =
        (item.plan_name || "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        (item.plan_key || "")
          .toLowerCase()
          .includes(search.toLowerCase());

      const matchesStatus =
        !status || item.plan_status === status;

      return matchesSearch && matchesStatus;
    });
  }, [plans, search, status]);

  const savePlan = async (event) => {
    event.preventDefault();

    const data = Object.fromEntries(new FormData(event.currentTarget));


    const validationErrors = validate(data, rules);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const payload = {
      plan_key: data.plan_key,
      plan_name: data.plan_name,
      plan_description: data.plan_description,
      plan_price: Number(data.plan_price),
      plan_billing_cycle: data.plan_billing_cycle,
      plan_status: data.plan_status,
      plan_limits: {
        creatives: Number(data.creatives),
        brands: Number(data.brands),
        campaigns: Number(data.campaigns),
        users: Number(data.users),
      },
    };

    try {
      if (form?.plan_id) {
        await api.put(`/plan/${form.plan_id}`, payload);
      } else {
        await api.post("/plan/save", payload);
      }

      setForm(null);
      loadPlans();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePlan = async () => {
    if (!deletePlan) return;

    try {
      await api.delete(`/plan/${deletePlan.plan_id}`);

      setDeletePlan(null);
      loadPlans();

      success("Plan deleted successfully");
    } catch (err) {
      console.error(err);
      error("Failed to delete plan");
    }
  };

  const handleValidationFormChange = (e) => {
    const { name, value } = e.target;

    setErrors((prev) => {
      if (!prev[name]) return prev;

      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };


  const PlanStatus = ({ value }) => (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold ${value === "ACTIVE"
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"
        }`}
    >
      {value}
    </span>
  );


  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h2 className="text-2xl font-bold">Plans</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage subscription limits, prices, and availability.
          </p>
        </div>
        {/* <button
          onClick={() => setForm({})}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white"
        >
          <Plus size={17} />
          Create plan
        </button> */}
        <button
          onClick={() => setForm({})}
          className="inline-flex items-center gap-2 rounded-lg bg-[#ff6968] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#e95555]"
        >
          <Plus size={17} />
          Create plan
        </button>
      </div>
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex gap-3 border-b border-slate-100 p-4">
          <label className="relative flex-1">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plans…"
              // className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm"
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm font-medium text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-[#ff696855] focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff69681a]"
            />
          </label>

          {/* <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 text-sm"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
          </select> */}

          <div className="status-dropdown relative min-w-[150px]">
            <button
              type="button"
              onClick={() => setIsStatusOpen((prev) => !prev)}
              className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 shadow-sm outline-none transition hover:border-[#ff696855] focus:border-[#ff6968]"
            >
              <span className="truncate">
                {status || "All Status"}
              </span>

              <ChevronDown
                size={17}
                className={`shrink-0 text-slate-400 transition-transform duration-200 ${isStatusOpen ? "rotate-180" : ""
                  }`}
              />
            </button>

            {isStatusOpen && (
              <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
                {[
                  { value: "", label: "All Status" },
                  { value: "ACTIVE", label: "ACTIVE" },
                  { value: "INACTIVE", label: "INACTIVE" },
                ].map((option) => {
                  const isSelected = status === option.value;

                  return (
                    <button
                      key={option.value || "all"}
                      type="button"
                      onClick={() => {
                        setStatus(option.value);
                        setIsStatusOpen(false);
                      }}
                      className={`mt-0.5 flex w-full items-center rounded-lg px-3 py-2.5 text-left text-sm transition ${isSelected
                        ? "bg-[#FFE2DE] font-semibold text-[#e95555]"
                        : "text-slate-600 hover:bg-[#FFE2DE] hover:text-[#e95555]"
                        }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {[
                  "Plan",
                  "Users",
                  "Creatives",
                  "Campaigns",
                  "Brands",
                  "Status",
                  "Actions",
                ].map((x) => (
                  <th key={x} className="px-5 py-3">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={8} className="py-10 text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredPlans.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center">
                    No plans found.
                  </td>
                </tr>
              )}

              {!loading &&
                filteredPlans.map((plan) => (
                  <tr key={plan.plan_id} className="hover:bg-slate-50">

                    <td className="px-5 py-4">
                      <div>
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${{
                            FREE: "bg-green-100 text-green-700",
                            STARTER: "bg-blue-100 text-blue-700",
                            PRO: "bg-violet-100 text-violet-700",
                            ENTERPRISE: "bg-amber-100 text-amber-700",
                          }[plan.plan_key] || "bg-slate-100 text-slate-700"
                            }`}
                        >
                          {plan.plan_key}
                        </span>

                        <div className="mt-2 font-semibold">
                          {plan.plan_name}
                        </div>

                        <div className="text-xs text-slate-500">
                          ₹ {plan.plan_price} / {plan.plan_billing_cycle}
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      {plan.plan_limits?.users}
                    </td>

                    <td className="px-5 py-4">
                      {plan.plan_limits?.creatives}
                    </td>

                    <td className="px-5 py-4">
                      {plan.plan_limits?.campaigns}
                    </td>

                    <td className="px-5 py-4">
                      {plan.plan_limits?.brands}
                    </td>

                    <td className="px-5 py-4">
                      <PlanStatus value={plan.plan_status} />
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">

                        <button
                          onClick={() => setForm(plan)}
                          className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          onClick={() => setDeletePlan(plan)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>
                    </td>

                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
      {deletePlan && (
        <DeleteConfirmation
          value={deletePlan}
          onClose={() => setDeletePlan(null)}
          onDelete={handleDeletePlan}
        />
      )}
      {form && (
        <PlanForm
          value={form}
          onClose={() => setForm(null)}
          onSave={savePlan}
          errors={errors}
          handleFormChange={handleValidationFormChange}
        />
      )}
    </div>
  );
}

function DeleteConfirmation({ value, onClose, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">

      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">

        <div className="border-b px-6 py-5">
          <h3 className="text-xl font-semibold text-red-600">
            Delete Plan
          </h3>

          <p className="mt-2 text-sm text-slate-500">
            Are you sure you want to delete
            <span className="font-semibold text-slate-800">
              {" "}{value.plan_name}
            </span>
            ?
          </p>

          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            This action cannot be undone.
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4">

          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-5 py-2 hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            onClick={onDelete}
            className="rounded-lg bg-red-600 px-5 py-2 font-semibold text-white hover:bg-red-700"
          >
            Delete Plan
          </button>

        </div>

      </div>

    </div>
  );
}


function PlanForm({ value = {}, onClose, onSave, errors, handleFormChange }) {
  return (
    // <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 h-full !mt-0">
    //   <form
    //     onSubmit={onSave}
    //     className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl"
    //   >
    //     {/* Header */}
    //     <div className="flex items-center justify-between border-b px-6 py-4">
    //       <div>
    //         <h3 className="text-xl font-semibold">
    //           {value.plan_id ? "Edit Plan" : "Create Plan"}
    //         </h3>

    //         <p className="mt-1 text-sm text-slate-500">
    //           Configure subscription plan details and limits.
    //         </p>
    //       </div>

    //       <button
    //         type="button"
    //         onClick={onClose}
    //         className="rounded-lg p-2 hover:bg-slate-100"
    //       >
    //         <X size={18} />
    //       </button>
    //     </div>

    //     {/* Body */}
    //     <div className="grid gap-5 p-6 sm:grid-cols-2">

    //       {/* Plan Key */}
    //       <div>
    //         <label className="mb-2 block text-sm font-medium">
    //           Plan Type
    //         </label>

    //         <select
    //           name="plan_key"
    //           defaultValue={value.plan_key || "FREE"}
    //           onChange={handleFormChange}
    //           className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
    //         >
    //           <option value="FREE">FREE</option>
    //           <option value="STARTER">STARTER</option>
    //           <option value="PRO">PRO</option>
    //           <option value="ENTERPRISE">ENTERPRISE</option>
    //         </select>
    //         <ErrorMessage error={errors?.plan_key} />
    //       </div>

    //       {/* Plan Name */}
    //       <div>
    //         <label className="mb-2 block text-sm font-medium">
    //           Plan Name
    //         </label>

    //         <input
    //           name="plan_name"
    //           defaultValue={value.plan_name || ""}
    //           onChange={handleFormChange}
    //           className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
    //         />
    //         <ErrorMessage error={errors?.plan_name} />
    //       </div>

    //       {/* Price */}
    //       <div>
    //         <label className="mb-2 block text-sm font-medium">
    //           Price
    //         </label>

    //         <input
    //           type="number"
    //           name="plan_price"
    //           defaultValue={value.plan_price || 0}
    //           onChange={handleFormChange}
    //           className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
    //         />
    //         <ErrorMessage error={errors?.plan_price} />
    //       </div>

    //       {/* Billing Cycle */}
    //       <div>
    //         <label className="mb-2 block text-sm font-medium">
    //           Billing Cycle
    //         </label>

    //         <select
    //           name="plan_billing_cycle"
    //           defaultValue={value.plan_billing_cycle || "YEARLY"}
    //           onChange={handleFormChange}
    //           className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
    //         >
    //           <option value="MONTHLY">MONTHLY</option>
    //           <option value="YEARLY">YEARLY</option>
    //         </select>
    //         <ErrorMessage error={errors?.plan_billing_cycle} />
    //       </div>

    //       {/* Description */}
    //       <div className="sm:col-span-2">
    //         <label className="mb-2 block text-sm font-medium">
    //           Description
    //         </label>

    //         <textarea
    //           rows={3}
    //           name="plan_description"
    //           defaultValue={value.plan_description || ""}
    //           onChange={handleFormChange}
    //           className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
    //         />
    //         <ErrorMessage error={errors?.plan_description} />
    //       </div>

    //       {/* Limits */}

    //       {[
    //         ["users", "Users"],
    //         ["creatives", "Creatives"],
    //         ["campaigns", "Campaigns"],
    //         ["brands", "Brands"],
    //       ].map(([key, label]) => (
    //         <div key={key}>
    //           <label className="mb-2 block text-sm font-medium">
    //             {label} Limit
    //           </label>

    //           <input
    //             type="number"
    //             min={0}
    //             name={key}
    //             onChange={handleFormChange}
    //             defaultValue={value.plan_limits?.[key] ?? 0}
    //             className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
    //           />
    //           <ErrorMessage error={errors[key]} />
    //         </div>
    //       ))}

    //       {/* Status */}
    //       <div>
    //         <label className="mb-2 block text-sm font-medium">
    //           Status
    //         </label>

    //         <select
    //           name="plan_status"
    //           defaultValue={value.plan_status || "ACTIVE"}
    //           onChange={handleFormChange}
    //           className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
    //         >
    //           <option value="ACTIVE">ACTIVE</option>
    //           <option value="INACTIVE">INACTIVE</option>
    //         </select>
    //         <ErrorMessage error={errors?.plan_status} />
    //       </div>

    //     </div>

    //     {/* Footer */}
    //     <div className="flex justify-end gap-3 border-t px-6 py-4">
    //       <button
    //         type="button"
    //         onClick={onClose}
    //         className="rounded-lg border px-5 py-2.5"
    //       >
    //         Cancel
    //       </button>

    //       <button
    //         type="submit"
    //         className="rounded-lg bg-indigo-600 px-5 py-2.5 font-semibold text-white hover:bg-indigo-700"
    //       >
    //         {value.plan_id ? "Update Plan" : "Create Plan"}
    //       </button>
    //     </div>
    //   </form>
    // </div>

    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[3px] p-4 !m-0 !h-full">
      <form
        onSubmit={onSave}
        className="flex w-full max-w-3xl max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">
              {value.plan_id ? "Edit Plan" : "Create Plan"}
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Configure subscription plan details and limits.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="grid gap-5 p-6 sm:grid-cols-2">

            {/* Plan Key */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Plan Type
              </label>

              <select
                name="plan_key"
                defaultValue={value.plan_key || "FREE"}
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff696833]"
              >
                <option value="FREE">FREE</option>
                <option value="STARTER">STARTER</option>
                <option value="PRO">PRO</option>
                <option value="ENTERPRISE">ENTERPRISE</option>
              </select>

              <ErrorMessage error={errors?.plan_key} />
            </div>

            {/* Plan Name */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Plan Name
              </label>

              <input
                name="plan_name"
                defaultValue={value.plan_name || ""}
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff696833]"
              />

              <ErrorMessage error={errors?.plan_name} />
            </div>

            {/* Price */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Price
              </label>

              <input
                type="number"
                name="plan_price"
                defaultValue={value.plan_price || 0}
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff696833]"
              />

              <ErrorMessage error={errors?.plan_price} />
            </div>

            {/* Billing Cycle */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Billing Cycle
              </label>

              <select
                name="plan_billing_cycle"
                defaultValue={value.plan_billing_cycle || "YEARLY"}
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff696833]"
              >
                <option value="MONTHLY">MONTHLY</option>
                <option value="YEARLY">YEARLY</option>
              </select>

              <ErrorMessage error={errors?.plan_billing_cycle} />
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Description
              </label>

              <textarea
                rows={3}
                name="plan_description"
                defaultValue={value.plan_description || ""}
                onChange={handleFormChange}
                className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff696833]"
              />

              <ErrorMessage error={errors?.plan_description} />
            </div>

            {/* Limits */}
            {[
              ["users", "Users"],
              ["creatives", "Creatives"],
              ["campaigns", "Campaigns"],
              ["brands", "Brands"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  {label} Limit
                </label>

                <input
                  type="number"
                  min={0}
                  name={key}
                  onChange={handleFormChange}
                  defaultValue={value.plan_limits?.[key] ?? 0}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff696833]"
                />

                <ErrorMessage error={errors[key]} />
              </div>
            ))}

            {/* Status */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Status
              </label>

              <select
                name="plan_status"
                defaultValue={value.plan_status || "ACTIVE"}
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-[#ff6968] focus:ring-2 focus:ring-[#ff696833]"
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>

              <ErrorMessage error={errors?.plan_status} />
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="rounded-lg bg-[#ff6968] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#e95555] focus:outline-none focus:ring-2 focus:ring-[#ff696855]"
          >
            {value.plan_id ? "Update Plan" : "Create Plan"}
          </button>
        </div>
      </form>
    </div>
  );
}
